import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { Search, Map as MapIcon, List, Check, Globe, Building2, Factory, Waves } from 'lucide-react';
interface Region {
  id: string;
  name: string;
  type: 'Country' | 'Market' | 'City' | 'Offshore';
  parentId?: string;
}
const REGIONS: Region[] = [
// Markets
{
  id: 'nordic',
  name: 'Nordic',
  type: 'Market'
}, {
  id: 'iberia',
  name: 'Iberian Peninsula',
  type: 'Market'
}, {
  id: 'cee',
  name: 'Central Eastern Europe',
  type: 'Market'
}, {
  id: 'balkans',
  name: 'Balkans',
  type: 'Market'
},
// Countries
{
  id: 'de',
  name: 'Germany',
  type: 'Country'
}, {
  id: 'uk',
  name: 'United Kingdom',
  type: 'Country'
}, {
  id: 'fr',
  name: 'France',
  type: 'Country'
}, {
  id: 'es',
  name: 'Spain',
  type: 'Country'
}, {
  id: 'it',
  name: 'Italy',
  type: 'Country'
}, {
  id: 'nl',
  name: 'Netherlands',
  type: 'Country'
}, {
  id: 'pl',
  name: 'Poland',
  type: 'Country'
}, {
  id: 'se',
  name: 'Sweden',
  type: 'Country'
}, {
  id: 'no',
  name: 'Norway',
  type: 'Country'
},
// Cities
{
  id: 'berlin',
  name: 'Berlin',
  type: 'City',
  parentId: 'de'
}, {
  id: 'london',
  name: 'London',
  type: 'City',
  parentId: 'uk'
}, {
  id: 'paris',
  name: 'Paris',
  type: 'City',
  parentId: 'fr'
}, {
  id: 'madrid',
  name: 'Madrid',
  type: 'City',
  parentId: 'es'
}, {
  id: 'rome',
  name: 'Rome',
  type: 'City',
  parentId: 'it'
}, {
  id: 'amsterdam',
  name: 'Amsterdam',
  type: 'City',
  parentId: 'nl'
}, {
  id: 'warsaw',
  name: 'Warsaw',
  type: 'City',
  parentId: 'pl'
}, {
  id: 'stockholm',
  name: 'Stockholm',
  type: 'City',
  parentId: 'se'
}, {
  id: 'oslo',
  name: 'Oslo',
  type: 'City',
  parentId: 'no'
}, {
  id: 'munich',
  name: 'Munich',
  type: 'City',
  parentId: 'de'
}, {
  id: 'hamburg',
  name: 'Hamburg',
  type: 'City',
  parentId: 'de'
},
// Offshore
{
  id: 'north_sea',
  name: 'North Sea',
  type: 'Offshore'
}, {
  id: 'baltic_sea',
  name: 'Baltic Sea',
  type: 'Offshore'
}, {
  id: 'mediterranean',
  name: 'Mediterranean',
  type: 'Offshore'
}];
interface RegionSelectorProps {
  value: string;
  onChange: (region: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
}
export function RegionSelector({
  value,
  onChange,
  onClose,
  style
}: RegionSelectorProps) {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'list' | 'map'>('list');
  const inputRef = useRef<HTMLInputElement>(null);
  // Auto-focus search on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  const filteredRegions = useMemo(() => {
    if (!search) return REGIONS;
    const lower = search.toLowerCase();
    return REGIONS.filter(r => r.name.toLowerCase().includes(lower));
  }, [search]);
  const groupedRegions = useMemo(() => {
    const groups: Record<string, Region[]> = {};
    filteredRegions.forEach(r => {
      if (!groups[r.type]) groups[r.type] = [];
      groups[r.type].push(r);
    });
    return groups;
  }, [filteredRegions]);
  const getIconForType = (type: string) => {
    switch (type) {
      case 'Market':
        return <Globe size={12} className="text-blue-400" data-id="element-1942" />;
      case 'Country':
        return <Globe size={12} className="text-emerald-400" data-id="element-1943" />;
      case 'City':
        return <Building2 size={12} className="text-amber-400" data-id="element-1944" />;
      case 'Offshore':
        return <Waves size={12} className="text-cyan-400" data-id="element-1945" />;
      default:
        return <Globe size={12} data-id="element-1946" />;
    }
  };
  // Simple SVG Map Component for "Map Selection" area
  const SimpleMap = () => <div className="relative w-full aspect-[4/3] bg-[#0F172A] rounded-lg overflow-hidden border border-white/[0.06] p-4 flex items-center justify-center group" data-id="element-1947">
      <svg viewBox="0 0 800 600" className="w-full h-full opacity-60 group-hover:opacity-100 transition-opacity" data-id="element-1948">
        {/* Simplified Europe Map Paths - Abstract representation */}
        <path d="M350,450 L380,480 L420,460 L400,420 Z" fill={value === 'Spain' ? '#EC4899' : '#334155'} className="hover:fill-[#EC4899] cursor-pointer transition-colors" onClick={() => {
        onChange('Spain');
        onClose();
      }} data-id="element-1949" />
        <path d="M400,420 L440,410 L460,450 L420,460 Z" fill={value === 'France' ? '#EC4899' : '#334155'} className="hover:fill-[#EC4899] cursor-pointer transition-colors" onClick={() => {
        onChange('France');
        onClose();
      }} data-id="element-1950" />
        <path d="M440,410 L480,400 L490,440 L460,450 Z" fill={value === 'Germany' ? '#EC4899' : '#334155'} className="hover:fill-[#EC4899] cursor-pointer transition-colors" onClick={() => {
        onChange('Germany');
        onClose();
      }} data-id="element-1951" />
        <path d="M380,350 L420,360 L410,400 L370,390 Z" fill={value === 'United Kingdom' ? '#EC4899' : '#334155'} className="hover:fill-[#EC4899] cursor-pointer transition-colors" onClick={() => {
        onChange('United Kingdom');
        onClose();
      }} data-id="element-1952" />
        <path d="M480,300 L520,310 L510,380 L470,370 Z" fill={value === 'Nordic' ? '#EC4899' : '#334155'} className="hover:fill-[#EC4899] cursor-pointer transition-colors" onClick={() => {
        onChange('Nordic');
        onClose();
      }} data-id="element-1953" />
        {/* Labels */}
        <text x="360" y="470" fill="white" fontSize="12" pointerEvents="none" data-id="element-1954">
          ES
        </text>
        <text x="430" y="440" fill="white" fontSize="12" pointerEvents="none" data-id="element-1955">
          FR
        </text>
        <text x="470" y="425" fill="white" fontSize="12" pointerEvents="none" data-id="element-1956">
          DE
        </text>
        <text x="390" y="380" fill="white" fontSize="12" pointerEvents="none" data-id="element-1957">
          UK
        </text>
        <text x="490" y="340" fill="white" fontSize="12" pointerEvents="none" data-id="element-1958">
          Nordic
        </text>
      </svg>
      <div className="absolute bottom-2 right-2 text-[10px] text-white/30" data-id="element-1959">
        Interactive Map Selection
      </div>
    </div>;
  return <div data-portal-menu className="jua-region-dropdown fixed w-[400px] rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden flex flex-col" style={{
    ...style,
    background: 'rgba(15, 23, 42, 0.95)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    maxHeight: '500px'
  }} data-id="element-1960">
      <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
      background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
    }} data-id="element-1961" />

      {/* Header / Tabs */}
      <div className="relative flex items-center justify-between px-2 pt-2 border-b border-white/[0.06]" data-id="element-1962">
        <div className="flex gap-1" data-id="element-1963">
          <button onClick={() => setActiveTab('list')} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${activeTab === 'list' ? 'bg-white/[0.06] text-white border-t border-x border-white/[0.08]' : 'text-white/50 hover:text-white/80'}`} data-id="element-1964">
            <List size={12} data-id="element-1965" /> List View
          </button>
          <button onClick={() => setActiveTab('map')} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${activeTab === 'map' ? 'bg-white/[0.06] text-white border-t border-x border-white/[0.08]' : 'text-white/50 hover:text-white/80'}`} data-id="element-1966">
            <MapIcon size={12} data-id="element-1967" /> Map Selection
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative flex flex-col flex-1 overflow-hidden bg-white/[0.02]" data-id="element-1968">
        {activeTab === 'list' ? <>
            {/* Search Bar */}
            <div className="p-3 border-b border-white/[0.06]" data-id="element-1969">
              <div className="relative" data-id="element-1970">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-1971" />
                <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cities, countries, or regions..." className="w-full h-8 pl-8 pr-3 rounded-lg bg-black/20 border border-white/[0.08] text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#EC4899]/50 focus:ring-1 focus:ring-[#EC4899]/20 transition-all" data-id="element-1972" />
              </div>
            </div>

            {/* Results List */}
            <div className="flex-1 overflow-y-auto" data-id="element-1973">
              {Object.keys(groupedRegions).length === 0 ? <div className="py-8 text-center text-xs text-white/30" data-id="element-1974">
                  No regions found matching "{search}"
                </div> : ['Market', 'Country', 'City', 'Offshore'].map(type => {
            const regions = groupedRegions[type];
            if (!regions?.length) return null;
            return <div key={type} data-id="element-1975">
                      <div className="px-3 text-[9px] font-bold text-white/30 uppercase tracking-widest sticky top-0 py-2 z-10 bg-[#0F172A] border-b border-white/[0.04]" data-id="element-1976">
                        {type === 'Market' ? 'Market Regions' : type === 'Offshore' ? 'Offshore Zones' : type === 'Country' ? 'Countries' : 'Cities'}
                      </div>
                      <div className="grid grid-cols-1 gap-0.5 p-1.5" data-id="element-1977">
                        {regions.map(region => <button key={region.id} onClick={() => {
                  onChange(region.name);
                  onClose();
                }} className={`group flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all ${value === region.name ? 'bg-[#EC4899]/10 text-white' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'}`} data-id="element-1978">
                            <div className="flex items-center gap-2.5" data-id="element-1979">
                              {getIconForType(region.type)}
                              <span className="text-xs font-medium" data-id="element-1980">
                                {region.name}
                              </span>
                              {region.parentId && <span className="text-[10px] text-white/30 group-hover:text-white/50" data-id="element-1981">
                                  {REGIONS.find(r => r.id === region.parentId)?.name}
                                </span>}
                            </div>
                            {value === region.name && <Check size={12} className="text-[#EC4899]" data-id="element-1982" />}
                          </button>)}
                      </div>
                    </div>;
          })}
            </div>
          </> : <div className="p-4 flex flex-col items-center justify-center h-full" data-id="element-1983">
            <SimpleMap data-id="element-1984" />
            <p className="mt-4 text-xs text-center text-white/50 max-w-[280px]" data-id="element-1985">
              Click on a region in the map above to select it.
              <br data-id="element-1986" />
              <span className="text-[10px] text-white/30" data-id="element-1987">
                (Simplified visualization)
              </span>
            </p>
          </div>}
      </div>

      {/* Footer */}
      <div className="relative px-3 py-2 border-t border-white/[0.06] bg-white/[0.02] flex justify-between items-center" data-id="element-1988">
        <span className="text-[10px] text-white/40" data-id="element-1989">
          {filteredRegions.length} regions available
        </span>
        <div className="flex gap-1" data-id="element-1990">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400/50" title="Markets" data-id="element-1991" />
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/50" title="Countries" data-id="element-1992" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400/50" title="Cities" data-id="element-1993" />
        </div>
      </div>
    </div>;
}